import React, { useState } from "react";
import { FaShieldAlt, FaUserCheck, FaGraduationCap, FaHeart, FaPhone, FaEnvelope, FaComments, FaPaperPlane } from "react-icons/fa";

const SupportPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "", 
    description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to Google Sheets via SheetDB  
      // TODO: Replace with your actual SheetDB API endpoint after creating it at sheetdb.io
      // Your Google Sheet: https://docs.google.com/spreadsheets/d/1fOWuu0VE4xhIIQ46_rOmvw8yHKpxpNHFjf6EDbhedpY
      const response = await fetch("https://sheetdb.io/api/v1/YOUR_CONTACT_API_ID", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: {
            Timestamp: new Date().toISOString(),
            Name: formData.name,
            Email: formData.email,
            Type: "Contact Form",
            Description: formData.description
          }
        })
      });

      if (response.ok) {
        // Send auto-reply email (this would typically be handled server-side)
        // For now, we'll just show success message
        setIsSubmitted(true);
        setFormData({ name: "", email: "", description: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: "#251529",
        minHeight: "100vh",
        color: "white",
        fontFamily: "Inter, sans-serif",
      }}
    >
    <div style={{ padding: 22, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ 
        fontSize: "1.9rem", 
        fontWeight: "bold", 
        textAlign: "center", 
        marginBottom: "1.5rem",
        background: "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent"
      }}>
        Support & Safety
      </h2>
      <p style={{ 
        textAlign: "center", 
        fontSize: "1.2rem", 
        lineHeight: "1.6", 
        marginBottom: "1rem",
        opacity: 0.9,
        maxWidth: "700px",
        margin: "0 auto 1rem auto"
      }}>
        Your wellness journey deserves nothing less than excellence. We're here to ensure every experience exceeds your expectations.
      </p>
      
      {/* Decorative dotted line */}
      <div
        style={{
          textAlign: "center",
          margin: "24px auto",
          maxWidth: 500,
        }}
      >
        <div
          style={{
            borderTop: "2px dotted rgba(168, 111, 214, 0.7)",
            width: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-10px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#251529",
              padding: "0 20px",
              fontSize: "1.5rem",
            }}
          >
            🛡️
          </div>
        </div>
      </div>
    </div>

    <div style={{ padding: "0 32px 64px 32px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        
        {/* Trust & Safety Section */}
        <div style={{
          background: "rgba(168, 111, 214, 0.1)",
          borderRadius: "20px",
          padding: "40px",
          margin: "8px 0",
          border: "1px solid rgba(168, 111, 214, 0.2)"
        }}>
          <h2 style={{
            fontSize: "2rem",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "2rem",
            background: "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Why You Can Trust WellBnB<span style={{fontSize: '0.6em', verticalAlign: 'super'}}>™</span>
          </h2>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
            marginTop: "32px"
          }}>
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center"
            }}>
              <FaShieldAlt style={{ fontSize: "2.5rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "12px" }}>
                Impeccable Safety Standards
              </h3>
              <p style={{ opacity: 0.9, lineHeight: "1.6" }}>
                Every accommodation and experience meets our rigorous safety protocols. Your wellbeing is our top priority.
              </p>
            </div>
            
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center"
            }}>
              <FaUserCheck style={{ fontSize: "2.5rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "12px" }}>
                Personally Curated Partners
              </h3>
              <p style={{ opacity: 0.9, lineHeight: "1.6" }}>
                Each partner is hand-selected and thoroughly vetted. We personally visit and approve every location and service provider.
              </p>
            </div>
            
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center"
            }}>
              <FaGraduationCap style={{ fontSize: "2.5rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "12px" }}>
                Trained Concierges
              </h3>
              <p style={{ opacity: 0.9, lineHeight: "1.6" }}>
                Our concierges complete comprehensive training that emphasizes integrity, professionalism, and your unique wellness goals.
              </p>
            </div>
          </div>
          
          <div style={{
            textAlign: "center",
            marginTop: "32px",
            padding: "24px",
            background: "rgba(168, 111, 214, 0.1)",
            borderRadius: "12px"
          }}>
            <FaHeart style={{ fontSize: "2rem", color: "#e0b3ff", marginBottom: "16px" }} />
            <p style={{ 
              fontSize: "1.2rem", 
              fontWeight: "500",
              lineHeight: "1.7",
              opacity: 0.95
            }}>
              Your experience is never random—it's consistent, carefully crafted, and designed with your trust in mind. 
              Not everyone can open their doors to you; our partners earn that privilege through dedication to excellence.
            </p>
          </div>
        </div>

        {/* Contact Support Section */}
        <div style={{
          background: "rgba(168, 111, 214, 0.08)",
          borderRadius: "20px",
          padding: "40px",
          margin: "40px 0"
        }}>
          <h2 style={{
            fontSize: "2rem",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "2rem",
            background: "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Get Support
          </h2>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "24px",
            marginTop: "32px"
          }}>
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center",
              transition: "all 0.3s ease",
              cursor: "pointer"
            }}>
              <FaComments style={{ fontSize: "2rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.1rem", fontWeight: "bold", marginBottom: "8px" }}>
                Live Chat
              </h3>
              <p style={{ opacity: 0.9, fontSize: "0.95rem" }}>
                Get instant help from our support team
              </p>
              <p style={{ color: "#a86fd6", fontSize: "0.9rem", marginTop: "8px" }}>
                Available 24/7
              </p>
            </div>
            
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center",
              transition: "all 0.3s ease",
              cursor: "pointer"
            }}>
              <FaEnvelope style={{ fontSize: "2rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.1rem", fontWeight: "bold", marginBottom: "8px" }}>
                Email Support
              </h3>
              <p style={{ opacity: 0.9, fontSize: "0.95rem" }}>
                support@mywellbnb.com
              </p>
              <p style={{ color: "#a86fd6", fontSize: "0.9rem", marginTop: "8px" }}>
                Response within 2 hours
              </p>
            </div>
            
            <div style={{
              background: "rgba(168, 111, 214, 0.15)",
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center",
              transition: "all 0.3s ease",
              cursor: "pointer"
            }}>
              <FaPhone style={{ fontSize: "2rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ fontSize: "1.1rem", fontWeight: "bold", marginBottom: "8px" }}>
                Emergency Line
              </h3>
              <p style={{ opacity: 0.9, fontSize: "0.95rem" }}>
                1-800-WELLBNB
              </p>
              <p style={{ color: "#a86fd6", fontSize: "0.9rem", marginTop: "8px" }}>
                24/7 Emergency Support
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div style={{ marginTop: "48px" }}>
          <h2 style={{
            fontSize: "2rem",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "2rem",
            background: "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Frequently Asked Questions
          </h2>
          
          <div style={{ maxWidth: "700px", margin: "0 auto" }}>
            {[
              {
                q: "How do you ensure the safety of accommodations?",
                a: "Every property undergoes a comprehensive 47-point safety inspection including structural integrity, emergency protocols, and wellness facility standards."
              },
              {
                q: "What qualifications do your concierges have?",
                a: "All concierges complete our 40-hour certification program covering wellness practices, local expertise, emergency response, and guest service excellence."
              },
              {
                q: "How are partners selected?",
                a: "We personally visit each potential partner, verify credentials, check references, and ensure they meet our high standards for wellness, safety, and hospitality."
              },
              {
                q: "What if I need help during my stay?",
                a: "Your assigned concierge is available 24/7, plus our emergency support line connects you instantly to our care team anywhere in the world."
              }
            ].map((faq, index) => (
              <div key={index} style={{
                background: "rgba(168, 111, 214, 0.1)",
                borderRadius: "12px",
                padding: "20px",
                margin: "16px 0",
                border: "1px solid rgba(168, 111, 214, 0.2)"
              }}>
                <h4 style={{ 
                  fontSize: "1.1rem", 
                  fontWeight: "600", 
                  marginBottom: "12px",
                  color: "#e0b3ff"
                }}>
                  {faq.q}
                </h4>
                <p style={{ 
                  opacity: 0.9, 
                  lineHeight: "1.6",
                  fontSize: "0.95rem"
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Section */}
        <div style={{
          background: "rgba(168, 111, 214, 0.08)",
          borderRadius: "20px",
          padding: "40px",
          margin: "48px 0",
          maxWidth: "600px",
          marginLeft: "auto",
          marginRight: "auto"
        }}>
          <h2 style={{
            fontSize: "2rem",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "1rem",
            background: "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Contact Us
          </h2>
          <p style={{
            textAlign: "center",
            opacity: 0.9,
            marginBottom: "2rem",
            fontSize: "1rem"
          }}>
            Have a question or need assistance? Send us a message and we'll get back to you soon.
          </p>

          {isSubmitted ? (
            <div style={{
              textAlign: "center",
              padding: "32px",
              background: "rgba(168, 111, 214, 0.2)",
              borderRadius: "16px",
              border: "1px solid rgba(168, 111, 214, 0.3)"
            }}>
              <FaPaperPlane style={{ fontSize: "2rem", color: "#a86fd6", marginBottom: "16px" }} />
              <h3 style={{ color: "#e0b3ff", marginBottom: "8px" }}>Message Sent!</h3>
              <p style={{ opacity: 0.9 }}>
                Thank you for contacting us. We've received your message and will get back to you soon.
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  background: "rgba(168, 111, 214, 0.3)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#e0b3ff"
                }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(168, 111, 214, 0.3)",
                    background: "rgba(168, 111, 214, 0.1)",
                    color: "white",
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                  placeholder="Your full name"
                />
              </div>
              
              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#e0b3ff"
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(168, 111, 214, 0.3)",
                    background: "rgba(168, 111, 214, 0.1)",
                    color: "white",
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                  placeholder="your.email@example.com"
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                  color: "#e0b3ff"
                }}>
                  Message *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(168, 111, 214, 0.3)",
                    background: "rgba(168, 111, 214, 0.1)",
                    color: "white",
                    fontSize: "1rem",
                    resize: "vertical",
                    boxSizing: "border-box",
                    fontFamily: "Inter, sans-serif"
                  }}
                  placeholder="How can we help you today?"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "14px",
                  background: isSubmitting 
                    ? "rgba(168, 111, 214, 0.5)" 
                    : "linear-gradient(90deg, #a86fd6 0%, #e0b3ff 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.3s ease"
                }}
              >
                <FaPaperPlane />
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default SupportPage;